// src/pages/WorkerRideChatPage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import socket from "../socket";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");

// Normalize ID
const normalizeId = (id) =>
  typeof id === "string"
    ? id
    : id?._id || id?.id || (id != null ? String(id) : "");

// Get workerId
const getWorkerId = () => {
  const direct = localStorage.getItem("workerId");
  if (direct && direct !== "null" && direct !== "undefined") return direct;

  const raw = localStorage.getItem("worker");
  if (raw) {
    try {
      const w = JSON.parse(raw);
      return w._id || w.id || "";
    } catch {}
  }
  return "";
};

const WorkerRideChatPage = () => {
  const { rideId, customerId: rawCustomerId } = useParams();

  const workerId = normalizeId(getWorkerId());
  const customerId = normalizeId(rawCustomerId);

  const [ride, setRide] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [roomId, setRoomId] = useState("");

  const bottomRef = useRef(null);

  /* =========================================================
      Load ride + chat
  ========================================================= */
  useEffect(() => {
    if (!rideId || !workerId || !customerId) return;

    const load = async () => {
      try {
        const [rideRes, chatRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ride/preview/${rideId}`),
          axios.get(`${API_BASE}/api/ride-chat/${rideId}?customerId=${customerId}`),
        ]);

        const rideData = rideRes.data;
        setRide(rideData);

        setMessages(chatRes.data?.messages || []);

        // private room id
        const rid = normalizeId(rideId);
        const wid = normalizeId(workerId);
        const cid = normalizeId(customerId);

        const privateRoom = `${rid}-${cid}-${wid}`;
        setRoomId(privateRoom);

        // JOIN NEW PRIVATE ROOM EVENT
        socket.emit("join-private-ride-chat", { roomId: privateRoom });

      } catch (err) {
        console.error("❌ Worker chat load error:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [rideId, workerId, customerId]);

  /* =========================================================
      Receive Live Chat Messages
  ========================================================= */
  useEffect(() => {
    if (!roomId) return;

    const handle = (data) => {
      if (normalizeId(data.roomId) !== normalizeId(roomId)) return;

      const msg = data.message;

      setMessages((prev) => {
        if (
          prev.some(
            (m) =>
              m.timestamp === msg.timestamp &&
              m.text === msg.text &&
              normalizeId(m.sender) === normalizeId(msg.sender)
          )
        )
          return prev;

        return [...prev, msg];
      });
    };

    socket.on("ride-message", handle);
    return () => socket.off("ride-message", handle);
  }, [roomId]);

  /* =========================================================
      Scroll Down
  ========================================================= */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* =========================================================
      Send Message
  ========================================================= */
  const sendMessage = async () => {
    const text = newMsg.trim();
    if (!text || !roomId) return;

    try {
      await axios.post(`${API_BASE}/api/ride-chat/send`, {
        rideId,
        roomId,
        sender: workerId,       // ⭐ FIXED
        senderModel: "Worker",
        text,
      });
      setNewMsg("");
    } catch (err) {
      console.error("❌ Error sending message:", err);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading chat…
      </div>
    );

  return (
    <div className="min-h-screen bg-blue-50 p-4 flex flex-col">
      {/* HEADER */}
      {ride && (
        <div className="mb-4 bg-white shadow p-4 rounded-lg">
          <h2 className="text-lg font-bold text-gray-900">Passenger Chat</h2>
          <p className="text-sm text-gray-600">
            {ride.from} → {ride.to}
          </p>
        </div>
      )}

      {/* MESSAGES */}
      <div className="flex-1 bg-white rounded-lg shadow p-4 overflow-y-auto space-y-3">
        {messages.map((m, i) => {
          const isMe =
            m.senderModel === "Worker" &&
            normalizeId(m.sender) === normalizeId(workerId);

          return (
            <div
              key={i}
              className={`max-w-sm p-3 rounded-lg text-sm ${
                isMe
                  ? "bg-green-600 text-white ml-auto"
                  : "bg-gray-100 text-gray-900 mr-auto"
              }`}
            >
              <p>{m.text}</p>
              <p className="text-xs opacity-70 mt-1 text-right">
                {m.timestamp ? format(new Date(m.timestamp), "p") : ""}
              </p>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="mt-4 flex gap-2">
        <input
          className="flex-1 p-3 rounded-full border border-gray-300"
          placeholder="Type a message…"
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
        <button
          onClick={sendMessage}
          className="bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default WorkerRideChatPage;
